import mixpanel from 'mixpanel-browser';
import { createContext, useContext, useEffect, useState } from 'react';

import { onConsentChange, shouldRunAnalytics } from '@utils/cookieConsent';

const MixpanelContext = createContext();

const MODULE_ALIASES = {
  Auth: 'auth',
  analysisLens: 'analysis_lens',
  chatwithanalysis: 'chat_with_analysis',
  dynamicSurvey: 'dynamic_surveys',
  interviewupload: 'interview_upload',
  paymentModal: 'payments',
  payment_upgrade: 'payments',
  creditsused: 'credits',
  creditsUsed: 'credits',
  creditspurchased: 'credits',
  credits_estimation: 'credits',
  surveyupload: 'survey_upload',
  survey: 'dynamic_surveys',
  tutorial_and_demos: 'tutorial',
};

const toSnakeCase = (value = '') =>
  String(value)
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .replace(/[\s./-]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '')
    .toLowerCase();

const canonicalizeModule = (moduleName) => {
  if (!moduleName) return 'unknown';
  const aliased = MODULE_ALIASES[moduleName] || moduleName;
  return toSnakeCase(aliased);
};

const getIntent = (moduleName, eventName) => {
  if (
    moduleName === 'runtime' ||
    ['javascript_error', 'unhandled_promise_rejection'].includes(eventName)
  ) {
    return 'reliability';
  }

  if (
    [
      'analysis_lens',
      'analysis_grounded_emergence',
      'chat_with_analysis',
      'dynamic_survey_details',
      'dynamic_survey_details_question_analysis',
      'dynamic_survey_details_theme',
      'code_details_dynamic_survey',
      'interview_details',
    ].includes(moduleName)
  ) {
    return 'analysis';
  }

  if (['payments', 'credits'].includes(moduleName)) {
    return 'commerce';
  }

  if (
    [
      'auth',
      'onboarding',
      'dynamic_surveys',
      'survey_upload',
      'interview',
      'interview_upload',
    ].includes(moduleName)
  ) {
    return 'funnel';
  }

  return 'behavior';
};

export const MixpanelProvider = ({ children }) => {
  const [isIdentified, setIsIdentified] = useState(false);
  const [userId, setUserId] = useState(null);
  const [isEnabled, setIsEnabled] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    const isStaging = import.meta.env.VITE_ENV === 'STAginG_Avoided';
    if (isStaging) {
      setIsEnabled(false);
      return undefined;
    }

    const tryInit = () => {
      if (!shouldRunAnalytics()) {
        setIsEnabled(false);
        return;
      }
      if (!isInitialized) {
        mixpanel.init(import.meta.env.VITE_MIXPANEL_TOKEN, {
          ignore_dnt: true,
          debug: import.meta.env.NODE_ENV !== 'production',
        });
        setIsInitialized(true);
      }
      setIsEnabled(true);
    };

    tryInit();
    const unsubscribe = onConsentChange((record) => {
      if (record.status === 'accepted') {
        tryInit();
      } else {
        setIsEnabled(false);
      }
    });
    return unsubscribe;
  }, [isInitialized]);

  // Track module-based events with consistent structure
  const trackEvent = (module, properties = {}) => {
    if (!isEnabled) return;
    const rawModule = module || properties.module || '';
    const rawEventName = properties.event_name || rawModule || 'unknown_event';
    const canonicalModule = canonicalizeModule(rawModule);
    const canonicalEventName = toSnakeCase(rawEventName) || 'unknown_event';
    const intent = getIntent(canonicalModule, canonicalEventName);

    // Standard properties that should be included in all events
    const standardProps = {
      timestamp: new Date().toISOString(),
      surface: 'researcher',
      environment: import.meta.env.VITE_ENV || import.meta.env.MODE || 'unknown',
      intent,
      release_sha: import.meta.env.VITE_RELEASE_SHA || import.meta.env.VITE_COMMIT_SHA || 'unknown',
    };

    // User identification props when available
    const userProps = isIdentified && userId ? { user_id: userId } : {};

    // Combine all properties with standard format
    const enhancedProps = {
      ...standardProps,
      ...userProps,
      ...properties,
      module: canonicalModule,
      canonical_event_name: canonicalEventName,
    };

    if (rawModule && canonicalModule !== rawModule) {
      enhancedProps.legacy_module = rawModule;
    }

    if (rawEventName && canonicalEventName !== rawEventName) {
      enhancedProps.legacy_event_name = rawEventName;
    }

    mixpanel.track(canonicalEventName, enhancedProps);
  };

  const identifyUser = (userId, properties = {}) => {
    if (!isEnabled) return;
    mixpanel.identify(userId);
    mixpanel.people.set({
      ...properties,
      $last_login: new Date().toISOString(),
    });
    setIsIdentified(true);
    setUserId(userId);
  };

  const aliasUser = (anonymousId, userId) => {
    if (!isEnabled) return;
    mixpanel.alias(userId, anonymousId);
    setIsIdentified(true);
    setUserId(userId);
  };
  const resetUser = () => {
    if (!isEnabled) return;
    mixpanel.reset();
    setIsIdentified(false);
    setUserId(null);
  };
  return (
    <MixpanelContext.Provider
      value={{ trackEvent, identifyUser, aliasUser, resetUser, isIdentified, userId }}
    >
      {children}
    </MixpanelContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useMixpanel = () => useContext(MixpanelContext);
